import React, { useState, useEffect, useCallback } from 'react';
import { emailAccountAPI } from '../../services/api';
import * as Icons from 'lucide-react';

const purposeColors = {
  global: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  reporting: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  critical_alert: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  otp: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  ip_whitelist: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
};

const purposeLabels = {
  global: 'Global',
  reporting: 'Reporting',
  critical_alert: 'Critical Alert',
  otp: 'OTP',
  ip_whitelist: 'IP Whitelist',
};

const emptyForm = {
  account_name: '', purpose: 'global', smtp_host: '', smtp_port: 587,
  username: '', password: '', from_name: 'WAY2SEND', from_email: '',
  use_tls: true, is_active: true, is_default: false,
};

const EmailAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [filterPurpose, setFilterPurpose] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testingId, setTestingId] = useState(null);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await emailAccountAPI.getAll(filterPurpose);
      setAccounts(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterPurpose]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const resetForm = () => { setForm({ ...emptyForm }); setEditId(null); setShowModal(false); };

  const handleEdit = (acct) => {
    setForm({
      account_name: acct.account_name, purpose: acct.purpose, smtp_host: acct.smtp_host,
      smtp_port: acct.smtp_port, username: acct.username, password: '',
      from_name: acct.from_name, from_email: acct.from_email, use_tls: acct.use_tls,
      is_active: acct.is_active, is_default: acct.is_default,
    });
    setEditId(acct.id); setShowModal(true);
  };

  const handleSubmit = async () => {
    setMsg({ type: '', text: '' });
    try {
      if (editId) {
        await emailAccountAPI.update(editId, form);
        setMsg({ type: 'success', text: 'Email account updated' });
      } else {
        await emailAccountAPI.create(form);
        setMsg({ type: 'success', text: 'Email account created' });
      }
      resetForm(); fetchAccounts();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Failed to save' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this email account?')) return;
    try {
      await emailAccountAPI.delete(id);
      fetchAccounts();
      setMsg({ type: 'success', text: 'Email account deleted' });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Failed to delete' });
    }
  };

  const handleTest = async (id) => {
    if (!testEmail) return;
    setTestingId(id);
    try {
      await emailAccountAPI.test(id, testEmail);
      setMsg({ type: 'success', text: `Test email sent to ${testEmail}` });
      setTestEmail(''); setTestingId(null);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Test failed' });
      setTestingId(null);
    }
  };

  const PurposeBadge = ({ purpose }) => {
    const c = purposeColors[purpose] || purposeColors.global;
    return (
      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${c.bg} ${c.text} ${c.border}`}>
        {purposeLabels[purpose] || purpose}
      </span>
    );
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Icons.Mail className="w-5 h-5 text-white" />
            </div>
            Email Accounts
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage SMTP email accounts for different purposes</p>
        </div>
        <button onClick={() => { resetForm(); setShowModal(true); }}
          className="px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-1.5">
          <Icons.Plus className="w-4 h-4" /> Add Account
        </button>
      </div>

      {msg.text && (
        <div className={`p-3 rounded-lg border text-sm flex items-center gap-2 ${msg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {msg.type === 'success' ? <Icons.CheckCircle className="w-4 h-4" /> : <Icons.AlertCircle className="w-4 h-4" />}
          {msg.text}
          <button onClick={() => setMsg({ type: '', text: '' })} className="ml-auto"><Icons.X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['', 'global', 'reporting', 'critical_alert', 'otp', 'ip_whitelist'].map(p => (
          <button key={p} onClick={() => { setFilterPurpose(p); setLoading(true); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${filterPurpose === p ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            {p === '' ? 'All' : purposeLabels[p] || p}
          </button>
        ))}
      </div>

      {/* Accounts Grid */}
      {loading ? (
        <div className="flex justify-center py-10"><Icons.Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Icons.Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No email accounts configured</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {accounts.map(acct => (
            <div key={acct.id} className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{acct.account_name}</h3>
                    <PurposeBadge purpose={acct.purpose} />
                    {acct.is_default && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-indigo-100 text-indigo-700 rounded-full border border-indigo-200">Default</span>
                    )}
                    {!acct.is_active && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-gray-100 text-gray-500 rounded-full border border-gray-200">Inactive</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 space-y-0.5">
                    <p><span className="font-medium text-gray-600">SMTP:</span> {acct.smtp_host}:{acct.smtp_port} {acct.use_tls ? '(TLS)' : ''}</p>
                    <p><span className="font-medium text-gray-600">From:</span> {acct.from_name} &lt;{acct.from_email}&gt;</p>
                    <p><span className="font-medium text-gray-600">User:</span> {acct.username}</p>
                    {acct.last_used_at && <p><span className="font-medium text-gray-600">Last Used:</span> {new Date(acct.last_used_at).toLocaleString()}</p>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(acct)} className="p-1.5 rounded-lg hover:bg-gray-100" title="Edit">
                    <Icons.Pencil className="w-4 h-4 text-gray-400" />
                  </button>
                  <button onClick={() => handleDelete(acct.id)} className="p-1.5 rounded-lg hover:bg-red-50" title="Delete">
                    <Icons.Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>

              {/* Test Email */}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                <input type="email" placeholder="test@example.com" value={testingId === acct.id ? testEmail : ''}
                  onChange={e => { setTestEmail(e.target.value); setTestingId(acct.id); }}
                  onFocus={() => setTestingId(acct.id)}
                  className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500" />
                <button onClick={() => handleTest(acct.id)} disabled={testingId === acct.id && !testEmail}
                  className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-semibold hover:bg-emerald-600 flex items-center gap-1 disabled:opacity-50">
                  {testingId === acct.id && testEmail === '' ? <Icons.Loader2 className="w-3 h-3 animate-spin" /> : <Icons.Send className="w-3 h-3" />}
                  Test
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">{editId ? 'Edit Email Account' : 'New Email Account'}</h2>
              <button onClick={resetForm} className="p-1.5 rounded-lg hover:bg-gray-100"><Icons.X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Account Name *</label>
                  <input type="text" value={form.account_name} onChange={e => setForm(f => ({ ...f, account_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" placeholder="e.g. Primary Gmail" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Purpose *</label>
                  <select value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500">
                    {Object.entries(purposeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">SMTP Host *</label>
                  <input type="text" value={form.smtp_host} onChange={e => setForm(f => ({ ...f, smtp_host: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" placeholder="smtp.gmail.com" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">SMTP Port</label>
                  <input type="number" value={form.smtp_port} onChange={e => setForm(f => ({ ...f, smtp_port: parseInt(e.target.value) || 587 }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Username *</label>
                  <input type="text" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" placeholder="user@gmail.com" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Password {editId ? '(leave blank to keep)' : '*'}</label>
                  <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">From Name</label>
                  <input type="text" value={form.from_name} onChange={e => setForm(f => ({ ...f, from_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" placeholder="WAY2SEND" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">From Email *</label>
                  <input type="email" value={form.from_email} onChange={e => setForm(f => ({ ...f, from_email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" placeholder="noreply@way2send.com" />
                </div>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.use_tls} onChange={e => setForm(f => ({ ...f, use_tls: e.target.checked }))}
                    className="w-4 h-4 text-indigo-600 rounded border-gray-300" />
                  <span className="text-sm text-gray-700">Use TLS</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                    className="w-4 h-4 text-indigo-600 rounded border-gray-300" />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_default} onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))}
                    className="w-4 h-4 text-indigo-600 rounded border-gray-300" />
                  <span className="text-sm text-gray-700">Default for purpose</span>
                </label>
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={resetForm} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={handleSubmit} disabled={!form.account_name || !form.smtp_host || !form.username || !form.from_email}
                className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                {editId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailAccounts;
